import * as DropdownMenu from '@wandb/weave/components/DropdownMenu';
import {Icon} from '@wandb/weave/components/Icon';
import copyToClipboard from 'copy-to-clipboard';
import React, {useCallback} from 'react';

import {useSelector} from '../../state/hooks';
import * as PanelBankSectionConfigTypes from '../../state/views/panelBankSectionConfig/types';
import {nwToast} from '../../state/workspaces/namedWorkspaceToast';
import {useDetectPanelVariant} from './hooks';
import {usePanelContext} from './PanelContextProvider';
import {getSharePanelLink} from './StandalonePanel';

export const PanelCopyLinkMenuItem = ({
  panelBankSectionConfigRef,
}: {
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref;
}) => {
  const {isFullScreenPanelPage} = useDetectPanelVariant();

  if (isFullScreenPanelPage) {
    return <FullScreenPanelCopyLinkMenuItem />;
  }

  return (
    <PanelBankCopyLinkMenuItem
      panelBankSectionConfigRef={panelBankSectionConfigRef}
    />
  );
};

const CopyLinkMenuItem = ({onSelect}: {onSelect: () => void}) => {
  return (
    <DropdownMenu.Item onSelect={onSelect}>
      <Icon name="link-alt" />
      Copy panel URL
    </DropdownMenu.Item>
  );
};

/**
 * Since we're already on the full screen panel page, we can just directly copy
 * the curren URL.
 */
const FullScreenPanelCopyLinkMenuItem = () => {
  const {trackPanelControls} = usePanelContext();

  const onSelect = useCallback(() => {
    copyToClipboard(window.location.href);
    nwToast('Copied to clipboard');

    trackPanelControls({action: 'Panel link copied'});
  }, [trackPanelControls]);

  return <CopyLinkMenuItem onSelect={onSelect} />;
};

/**
 * If we are trying to copy the link for a panel on a regular workspace, we need
 * generate the full screen panel link to be copied.
 */
const PanelBankCopyLinkMenuItem = ({
  panelBankSectionConfigRef,
}: {
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref;
}) => {
  const {panel, trackPanelControls} = usePanelContext();
  const panelBankSectionName = useSelector(state => {
    return state.views.parts[panelBankSectionConfigRef.type][
      panelBankSectionConfigRef.id
    ]?.name;
  });

  const onSelect = useCallback(() => {
    const sharePanelPath = getSharePanelLink(panel, panelBankSectionName);
    copyToClipboard(`${window.location.origin}${sharePanelPath}`);
    nwToast('Copied to clipboard');

    trackPanelControls({action: 'Panel link copied'});
  }, [panel, panelBankSectionName, trackPanelControls]);

  return <CopyLinkMenuItem onSelect={onSelect} />;
};
